import React from "react";
import styled from "styled-components";
import { useLocation } from "react-router-dom";

import { FullscreenLoader } from "./FullscreenLoader";
import { StyledButton } from "./StyledButton";
import { Theme } from "./Theme";

const ROUTE_CHUNK_RELOAD_STORAGE_KEY = "route_chunk_reload_attempt";
const ROUTE_CHUNK_RELOAD_WINDOW_MS = 30000;

type RouteChunkErrorBoundaryProps = {
  children: React.ReactNode;
};

type RouteChunkBoundaryInnerProps = {
  children: React.ReactNode;
  onError: (error: Error) => void;
  fallback: (error: Error) => React.ReactNode;
  resetKey: string;
};

type RouteChunkBoundaryInnerState = {
  error: Error | null;
};

export const isRecoverableRouteChunkError = (error: unknown) => {
  const message =
    error instanceof Error
      ? `${error.name} ${error.message}`
      : typeof error === "string"
        ? error
        : "";

  return /ChunkLoadError|Loading chunk [\d]+ failed|Failed to fetch dynamically imported module|Importing a module script failed/i.test(
    message
  );
};

const shouldAttemptRouteChunkReload = (pathname: string) => {
  try {
    const rawValue = window.sessionStorage.getItem(
      ROUTE_CHUNK_RELOAD_STORAGE_KEY
    );
    const parsedValue = rawValue ? JSON.parse(rawValue) : null;
    const previousPathname =
      typeof parsedValue?.pathname === "string" ? parsedValue.pathname : "";
    const previousTimestamp =
      typeof parsedValue?.timestamp === "number" ? parsedValue.timestamp : 0;
    const currentTimestamp = Date.now();

    if (
      previousPathname === pathname &&
      currentTimestamp - previousTimestamp < ROUTE_CHUNK_RELOAD_WINDOW_MS
    ) {
      return false;
    }

    window.sessionStorage.setItem(
      ROUTE_CHUNK_RELOAD_STORAGE_KEY,
      JSON.stringify({
        pathname,
        timestamp: currentTimestamp,
      })
    );

    return true;
  } catch {
    return true;
  }
};

class RouteChunkBoundaryInner extends React.Component<
  RouteChunkBoundaryInnerProps,
  RouteChunkBoundaryInnerState
> {
  state: RouteChunkBoundaryInnerState = {
    error: null,
  };

  static getDerivedStateFromError(error: Error) {
    return {
      error,
    };
  }

  componentDidCatch(error: Error) {
    this.props.onError(error);
  }

  componentDidUpdate(prevProps: RouteChunkBoundaryInnerProps) {
    if (
      prevProps.resetKey !== this.props.resetKey &&
      this.state.error !== null
    ) {
      this.setState({
        error: null,
      });
    }
  }

  render() {
    if (this.state.error) {
      return this.props.fallback(this.state.error);
    }

    return this.props.children;
  }
}

export const RouteChunkErrorBoundary: React.FC<
  RouteChunkErrorBoundaryProps
> = ({ children }) => {
  const location = useLocation();
  const [isReloading, setIsReloading] = React.useState(false);

  const handleError = React.useCallback(
    (error: Error) => {
      if (!isRecoverableRouteChunkError(error)) {
        return;
      }

      if (!shouldAttemptRouteChunkReload(location.pathname)) {
        return;
      }

      setIsReloading(true);
      window.location.reload();
    },
    [location.pathname]
  );

  const renderFallback = React.useCallback((error: Error) => {
    const isChunkError = isRecoverableRouteChunkError(error);

    return (
      <StyledErrorWrap>
        <StyledErrorCard>
          <StyledErrorTitle>
            {isChunkError
              ? "Обновляем приложение"
              : "Не удалось открыть страницу"}
          </StyledErrorTitle>

          <StyledErrorText>
            {isChunkError
              ? "Версия страницы устарела или загрузка прервалась. Попробуйте обновить страницу."
              : "Во время открытия страницы произошла ошибка. Попробуйте обновить ее еще раз."}
          </StyledErrorText>

          <StyledErrorActions>
            <StyledButton
              type="button"
              variant="accent"
              active
              onClick={() => window.location.reload()}
            >
              Обновить страницу
            </StyledButton>
          </StyledErrorActions>
        </StyledErrorCard>
      </StyledErrorWrap>
    );
  }, []);

  if (isReloading) {
    return <FullscreenLoader text="Обновляем приложение..." />;
  }

  return (
    <RouteChunkBoundaryInner
      resetKey={location.key || location.pathname}
      onError={handleError}
      fallback={renderFallback}
    >
      {children}
    </RouteChunkBoundaryInner>
  );
};

const StyledErrorWrap = styled.div`
  min-height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: ${Theme.colors.pageBgColor};

  @supports not (height: 100dvh) {
    min-height: 100vh;
  }
`;

const StyledErrorCard = styled.div`
  width: 100%;
  max-width: 430px;
  padding: 20px 18px;
  border-radius: 20px;
  border: 1px solid ${Theme.colors.borderColor};
  background:
    linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.03) 0%,
      rgba(255, 255, 255, 0.015) 100%
    ),
    linear-gradient(195deg, #111111 0%, ${Theme.colors.blockColor} 100%);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.04),
    0 10px 24px rgba(0, 0, 0, 0.18);
  text-align: center;
`;

const StyledErrorTitle = styled.h2`
  margin: 0;
  color: ${Theme.colors.mainFontColor};
  font-size: ${Theme.font.fontSize5};
  font-weight: ${Theme.font.fontWeight1};
  line-height: 1.25;
`;

const StyledErrorText = styled.p`
  margin: 10px 0 0;
  color: ${Theme.colors.secondaryFontColor};
  font-size: ${Theme.font.fontSize3};
  line-height: 1.55;
`;

const StyledErrorActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
`;
